import {html, LitElement, TemplateResult} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import configModel from 'gmfapi/store/config';
import './owner-request.css';
import {unsafeSVG} from 'lit-html/directives/unsafe-svg.js';
import {Subscription} from 'rxjs';
import {getOwnerByEGRID} from '../../terravis_api/terravis.ts';
import {msg, localized} from '@lit/localize';
import {
  Erwerbsgrund,
  GebaeudeWrapper,
  LegalPerson,
  NaturalPerson,
  Parcel,
  Person,
  PersonStammKey,
  Right,
  Stamm,
  Grundstueck,
} from './owner-request-interfaces.ts';
import gmfAuthenticationService from 'ngeo/auth/service';
import user from 'gmfapi/store/user';

window.gmfAuthenticationService = gmfAuthenticationService;

declare global {
  interface Window {
    onCaptchaSuccess: (token: string) => void;
    recaptchaReady?: boolean;
    gmfAuthenticationService?: any;
  }
}

@localized()
@customElement('owner-request-window')
export class OwnerRequestWindow extends LitElement {
  @property({type: String}) egrid: string = '';
  @state() parcelData?: Object = undefined;
  @state() personData?: Object = undefined;
  @state() rightData?: Object = undefined;
  @state() isLoggedIn: boolean | undefined = undefined;
  @state() loading: boolean = true;
  @state() generalError = false;
  @state() tooManyRequestsError = false;
  @state() tooManyRequestsInSubqueryError = false;
  @state() missingCaptchTokenError = false;
  @state() nightRestrictionError = false;
  @state() noResultError?: string = undefined;

  subscriptions: Subscription[] = [];
  spinnerSVG: string = '';
  terravisApiUrl: string = '';
  openIdConnectBaseUrl: string = '';
  requestNumberAnonymous: number;
  requestNumberLogged: number;
  mfa: boolean = false;

  @state() googleRecaptchaSiteKey: string | undefined = undefined;
  simpleEgrid: string = '';
  @state() parcels: Parcel[] | undefined = [];
  @state() result = false;
  @state() captchaToken: string = '';
  @state() captchaScriptLoaded: boolean = false;
  captchaWidgetId: number | undefined;

  constructor() {
    super();
    const configSubscription = configModel.getConfig().subscribe((config) => {
      if (!config) {
        return;
      }
      this.terravisApiUrl = config.terravisApiUrl ? `${config.terravisApiUrl}/` : '';
      this.requestNumberLogged = config.requestNumberLogged || '';
      this.requestNumberAnonymous = config.requestNumberAnonymous || '';
      this.openIdConnectBaseUrl = config.gmfOidcLoginUrl || '';
      this.googleRecaptchaSiteKey = config.recaptchaSiteKey || '';

      configSubscription.unsubscribe();
    });

    fetch(`${this.terravisApiUrl}terravis/amr`)
      .then((response) => response.json())
      .then((data) => {
        this.mfa = data.includes('mfa');
      })
      .catch((error) => {
        console.error('Error fetching MFA status:', error);
      });

    this.captchaScriptLoaded = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadSpinnerSVG();
    window.onCaptchaSuccess = (token: string) => this.onCaptchaSuccess(token);

    // Check if recaptchaReady was already set to true from the script load
    if (window.recaptchaReady === true) {
      this.captchaScriptLoaded = true;
    }

    // Add event listener for custom event from owner.html
    document.addEventListener('recaptchaLoaded', () => {
      this.captchaScriptLoaded = true;
    });

    setTimeout(() => {
      this.subscriptions.push(
        user.getProperties().subscribe((user: any) => {
          // The intranet users are the users from the cantonal networks, consider them as logged in to avoid the reCAPTCHA
          this.isLoggedIn = !!user.username || user.is_intranet;
          this.loading = false;
        }),
      );
    }, 1000);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onCaptchaSuccess(token: string) {
    this.missingCaptchTokenError = false;
    this.captchaToken = token;
  }

  resetCaptchaChallenge() {
    if (this.isLoggedIn || !window.grecaptcha || this.captchaWidgetId === undefined) {
      return;
    }
    try {
      window.grecaptcha.reset(this.captchaWidgetId);
    } catch (error) {
      console.error('Error resetting reCAPTCHA:', error);
    }
  }

  async sendRequest() {
    if (!this.captchaToken && !this.isLoggedIn) {
      this.missingCaptchTokenError = true;
      console.error('Captcha token is missing');
      return;
    }

    const captchaToken = this.captchaToken;
    this.generalError = false;
    this.tooManyRequestsError = false;
    this.tooManyRequestsInSubqueryError = false;
    this.nightRestrictionError = false;
    this.noResultError = undefined;
    this.loading = true;
    this.simpleEgrid = this.egrid.split(':')[0];
    try {
      let result = await getOwnerByEGRID(this.egrid, this.terravisApiUrl, captchaToken);
      if (!result || typeof result !== 'object') {
        this.generalError = true;
        console.error('No response or invalid response from backend');
        return;
      }
      if (result.ok) {
        this.result = true;
        const data = await result.json();
        if (!data) {
          this.generalError = true;
          console.error('error', 'no data');
        } else {
          if (!data.parcels || data.parcels.length === 0) {
            if (
              data.missing &&
              data.missing[0].__values__ &&
              data.missing[0].__values__.description &&
              data.missing[0].__values__.description.includes('Data not found')
            ) {
              const parcelNumber = this.egrid.split(':')[1];
              this.noResultError =
                msg('Kein Ergebnis verfügbar für EGRID') +
                ' ' +
                this.simpleEgrid +
                ' ' +
                msg('und Grundstücknummer') +
                ' ' +
                parcelNumber +
                '.';
            } else {
              this.generalError = true;
              console.error('Error', 'no data');
            }
          } else {
            this.generalError = false;
            this.tooManyRequestsError = false;
            this.tooManyRequestsInSubqueryError = false;
            this.noResultError = undefined;
            this.missingCaptchTokenError = false;
            this.parcels = data.parcels;
          }
        }
      } else {
        if (result.status === 429) {
          console.error('Too Many Requests', result);
          this.tooManyRequestsError = true;
        } else if (result.status === 503) {
          const data = await result.json();
          if (data?.reason === 'night_restriction') {
            this.nightRestrictionError = true;
          } else {
            console.error('Service unavailable:', result.statusText);
            this.generalError = true;
          }
        } else {
          console.error('Error:', result.statusText);
          this.generalError = true;
        }
      }
    } catch (err) {
      this.generalError = true;
      console.error('Unexpected error in sendRequest:', err);
    } finally {
      if (!this.isLoggedIn) {
        this.captchaToken = '';
        this.resetCaptchaChallenge();
      }
      this.loading = false;
    }
  }

  async loadSpinnerSVG() {
    try {
      const response = await fetch('static-frontend/spinner.svg');
      if (response.ok) {
        this.spinnerSVG = await response.text();
      } else {
        console.error('Failed to load spinner SVG:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading spinner SVG:', error);
    }
  }

  formatDate(dateArray: (number | null)[]): string {
    if (dateArray.length < 3) {
      return '';
    }
    const [year, month, day] = dateArray;
    if (year === null || month === null || day === null) {
      return '';
    }
    return `${day}.${month}.${year}`;
  }

  render() {
    let mainContent;
    let errorSection;
    let resultSection;
    let loginInfo;
    if (!this.result && !this.loading) {
      if (!this.mfa) {
        loginInfo = html` <div>
          ${msg('Sie sind nicht mit 2FA (Zwei-Faktor Authentifizierung) eingeloggt: Sie können pro 24h')}
          ${this.requestNumberAnonymous} ${msg('Anfragen starten.')}<br />
          ${msg('Wenn Sie sich mit 2FA einloggen, können Sie ')} ${this.requestNumberLogged}
          ${msg('Anfragen starten.')}
          <a
            href="${`${this.openIdConnectBaseUrl}?came_from=${encodeURIComponent(window.location.href)}`}"
            class="login-link"
            >Login</a
          >
        </div>`;
      } else {
        loginInfo = html` <div>
          ${msg('Sie sind eingeloggt: Sie können pro 24h')} ${this.requestNumberLogged}
          ${msg('Anfragen starten.')}
        </div>`;
      }
    }
    mainContent = html`
      <div>
        <div class="logo-image logo-geogr"></div>
        <h1>${msg('GeoGR Eigentümerabfrage')}</h1>
        <br />
        ${loginInfo}
        ${!this.result && !this.loading && !this.isLoggedIn ? this.renderCaptcha() : html`<br />`}
        ${!this.result && !this.loading
          ? html`
              <button
                class="owner-send-button btn"
                @click=${this.sendRequest}
                ?disabled=${!this.captchaToken && !this.isLoggedIn}
              >
                ${msg('Abfrage starten')}
              </button>
              <br />
            `
          : html``}
      </div>
    `;
    resultSection = html`
      ${!this.parcels && !this.loading
        ? html`<br />
            <div>${msg('Keine Resutlate verfügbar.')}</div>`
        : html``}
      ${this.loading ? this.renderSpinner() : html``}
      ${!this.loading && this.parcels ? this.renderResult() : html``}
    `;

    errorSection = html`
      ${this.tooManyRequestsInSubqueryError
        ? html`<p class="error">
            ${msg(
              'Es konnten nicht alle Eigentümer abgefragt werden, da die erlaubte Anzahl Anfragen überschritten wurde.',
            )}
          </p>`
        : html``}
      ${this.tooManyRequestsError
        ? html`<p class="error">${msg('Sie haben die erlaubte Anzahl Anfragen pro 24h überschritten!')}</p>`
        : html``}
      ${this.noResultError ? html`<p class="error">${this.noResultError}</p>` : html``}
      ${this.nightRestrictionError
        ? html`<p class="error">
            ${msg(
              'The owner query is temporarily unavailable during the nightly maintenance window. Please try again later.',
            )}
          </p>`
        : html``}
      ${this.generalError ? html`<p class="error">${msg('Es ist ein Fehler aufgetreten.')}</p>` : html``}
      ${this.missingCaptchTokenError ? html`<p class="error">${msg('Captcha-Token ungültig.')}</p>` : html``}
      <br />
    `;

    return html`
      <div class="owner-request-window">
        ${mainContent} ${resultSection} ${errorSection}
        ${this.parcels && this.parcels.length > 0
          ? html`<p>
              ${msg('Die Aktualität und die Richtigkeit der Daten kann nicht gewährleistet werden.')}
            </p>`
          : html``}
        <br />
      </div>
    `;
  }

  renderSpinner() {
    return html`
      <div class="owner-request-spinner">
        <i class="fa-solid custom-spinner-loading fa-spin spinner-loading-mask"
          >${unsafeSVG(this.spinnerSVG)}
        </i>
      </div>
    `;
  }

  renderResult() {
    return html` <br />
      <div>
        ${(() => {
          try {
            const parcel = this.parcels ? this.parcels[0] : undefined;
            return html`
              ${parcel?.grundstueck ? this.returnParcelData(parcel?.grundstueck) : html``}
              <br />
              ${parcel?.recht
                ? this.returnRightAndPersonData(parcel.recht, parcel.person!, parcel.grundstueck)
                : html``}
              <br />
            `;
          } catch (e) {
            console.error('Error rendering parcel/right/person data:', e);
            return html`<p class="error">${msg('Fehler beim Anzeigen der Eigentümerdaten.')}</p>`;
          }
        })()}
      </div>`;
  }

  returnParcelData(parcels: Parcel[]) {
    const parcelWithEgid = parcels.filter((parcel) => parcel.nummer?.includes(this.egrid));
    if (parcelWithEgid.length === 0) {
      return html``;
    }
    if (parcelWithEgid.length > 1) {
      this.generalError = true;
      console.error('error', 'too many grundstueck');
      return html``;
    } else {
      const parcel = parcelWithEgid[0];
      return html` <div>
        <h2>${msg('Grundstückbeschreibung')}</h2>
        <div class="data-table">
          <div class="label">E_GRID:</div>
          <div>${this.simpleEgrid}</div>
          <div class="label">${msg('Politische Gemeinde:')}</div>
          <div>${parcel.gemeinde?.gemeinde?.municipality_name}</div>
          <div class="label">${msg('BFS-Nr:')}</div>
          <div>${parcel.nummer?.split('::')[1]}</div>
          <div class="label">${msg('Grundbuchamt:')}</div>
          <div>${parcel.gbamt?.gbamt?.name}</div>
          <div class="label">${msg('Grundstück-Nr:')}</div>
          <div>${parcel.nummer?.split(':')[1]}</div>
          <div class="label">${msg('Fläche:')}</div>
          <div>
            ${parcel.inhalt_liegenschaft && parcel.inhalt_liegenschaft.length > 0
              ? parcel.inhalt_liegenschaft[0].flaechenmass
              : ''}
          </div>
          <div>
            ${parcel.gebaeude && parcel.gebaeude.length > 0
              ? html`<div class="label">${msg('Gebäude/Bauten:')}</div>`
              : html``}
            <ul class="owner-request-ul">
              ${parcel.gebaeude && parcel.gebaeude.length > 0
                ? parcel.gebaeude.map(
                    (gebaeude: GebaeudeWrapper) => html`
                      <li class="owner-request-li">
                        ${gebaeude.gebaeude?.gebaeudeart_zusatz ||
                        gebaeude.gebaeude?.gebaeudeart_stichwort?.gebaeude_gebaeudeart?.stichwort},
                        ${msg('Nummer:')} ${gebaeude.gebaeude?.nummer}
                        ${gebaeude.gebaeude?.adresse && gebaeude.gebaeude?.adresse.length > 0
                          ? `, ${gebaeude.gebaeude?.adresse[0].gebaeudeeingang_adresse?.strasse}, ${gebaeude.gebaeude?.adresse[0].gebaeudeeingang_adresse?.hausnummer}`
                          : ''}
                      </li>
                    `,
                  )
                : ''}
            </ul>
          </div>
        </div>
      </div>`;
    }
  }

  renderRawPerson(person: Person, allPersons: Person[]) {
    const stamm = person?.person_stamm;
    if (stamm) {
      return this.renderSinglePerson(stamm);
    }
    const members = person?.mitglieder as {value: string}[];
    if (members) {
      const art = person.inhalt_gemeinschaft?.length ? person.inhalt_gemeinschaft[0].art : undefined;
      const name = person.inhalt_gemeinschaft?.length ? person.inhalt_gemeinschaft[0].name : undefined;
      return this.renderMembers(members, allPersons, art, name); // Call the renderMembers method to handle members rendering
    }
    return html``;
  }

  renderMembers(
    members: {value: string}[],
    allPersons: Person[],
    art?: string,
    name?: string,
  ): TemplateResult | undefined {
    const renderedMembers = [];
    for (const member of members) {
      const foundPerson = allPersons.find((p: Person) => p.nummer && p.nummer === member.value);
      if (foundPerson?.person_stamm) {
        renderedMembers.push(this.renderSinglePerson(foundPerson.person_stamm));
      } else if (foundPerson?.mitglieder) {
        const art = foundPerson.inhalt_gemeinschaft?.length
          ? foundPerson.inhalt_gemeinschaft[0].art
          : undefined;
        const name = foundPerson.inhalt_gemeinschaft?.length
          ? foundPerson.inhalt_gemeinschaft[0].name
          : undefined;
        renderedMembers.push(this.renderMembers(foundPerson.mitglieder, allPersons, art, name));
      }
    }
    if (renderedMembers.length) {
      return html`
        <div>${art} ${name}:</div>
        <ul class="owner-request-ul">
          ${renderedMembers.map((item) => html`<li class="owner-request-li">${item}</li>`)}
        </ul>
      `;
    }
  }

  renderSinglePerson(stamm: Stamm) {
    const keys: PersonStammKey[] = [
      'natuerliche_person',
      'schweizerische_juristische_person',
      'oeffentliche_koerperschaft',
      'auslaendische_rechtsform',
      'juristische_person',
    ];
    for (const key of keys) {
      if (stamm[key]) {
        if (key === 'schweizerische_juristische_person') {
          return this.renderLegalPerson(stamm[key]);
        } else {
          return this.renderNaturalPerson(stamm[key]);
        }
      }
    }
    // Fallback: always return a Lit template
    return html``;
  }

  renderNaturalPerson(person: NaturalPerson) {
    const adresses = person.adresse && person.adresse.length > 0 ? person.adresse : [];
    const adresse = adresses.length > 0 ? adresses[0] : undefined;
    return html`
      <div>
        ${person.vorname} ${person.name}
        ${adresse
          ? `, ${adresse.adresse?.strasse}, ${adresse.adresse?.hausnummer}, ${adresse.adresse?.plz}, ${adresse.adresse?.ort}`
          : ''}
      </div>
    `;
  }

  renderLegalPerson(person: LegalPerson) {
    const adresses = person.adresse && person.adresse.length > 0 ? person.adresse : [];
    const adresse = adresses.length > 0 ? adresses[0] : undefined;
    return html`
      <div>
        ${person.name}, UID: ${person.uid}, ${msg('mit Sitz in')} ${person.sitz}
        ${adresse
          ? ` , ${adresse.adresse?.strasse}
        ${adresse.adresse?.hausnummer}, ${adresse.adresse?.plz}
        ${adresse.adresse?.ort}`
          : ''}
      </div>
    `;
  }

  eliminateDuplicates(objects: Object[]): Object[] {
    const uniqueObjects = new Set();
    const result: Object[] = [];
    objects.forEach((obj) => {
      const value = JSON.stringify(obj);
      if (!uniqueObjects.has(value)) {
        uniqueObjects.add(value);
        result.push(obj);
      }
    });
    return result;
  }

  returnRightAndPersonData(rights: Right[], persons: Person[], grundstueck?: Grundstueck[]) {
    const parts = rights.filter(
      (right) => right.inhalt_eigentum_anteil && right.inhalt_eigentum_anteil.length > 0,
    );
    return html`
      <div>
        <h2>Eigentum</h2>
        ${parts.map((right: Right) => {
          if (
            right.belastetes_grundstueck &&
            right.belastetes_grundstueck.split(':')[0] === this.simpleEgrid
          ) {
            let owners = persons.filter((p: Person) => p.nummer && p.nummer === right.berechtigte);
            if (!owners.length) {
              owners = right.coOwners || [];
            }
            let floorProperties = grundstueck?.filter(
              (g) =>
                g.nummer === right.berechtigte &&
                g.inhalt_stockwerks_einheit &&
                g.inhalt_stockwerks_einheit.length > 0,
            );

            return html`
              <div>
                ${!owners?.length ? right.berechtigte?.replace(/::/g, '/').replace(/:/g, '/') : ''}
                <div class="label">
                  ${right.inhalt_eigentum_anteil && right.inhalt_eigentum_anteil.length > 0
                    ? `${right.inhalt_eigentum_anteil[0].anteil_zaehler} /
                ${right.inhalt_eigentum_anteil[0].anteil_nenner},
                ${right.inhalt_eigentum_anteil[0].eigentumsform}`
                    : ''}
                </div>
                <div class="data-table purchase">
                  <div>Erwerbsdatum:</div>
                  <ul>
                    ${right.erwerbsgrund && right.erwerbsgrund.length > 0
                      ? right.erwerbsgrund.map(
                          (erwerbsgrund: Erwerbsgrund) => html`
                            <li class="owner-request-li">
                              ${this.formatDate(erwerbsgrund.erwerbsgrund?.datum)}
                            </li>
                          `,
                        )
                      : ''}
                  </ul>
                </div>
                ${(Array.isArray(owners) ? owners : []).map((owner) => this.renderRawPerson(owner, persons))}
                ${floorProperties && floorProperties.length > 0
                  ? html`
                      <div class="data-table description">
                        <div>Beschreibung:</div>
                        <ul>
                          ${floorProperties.map(
                            (floorProperty: Grundstueck) => html`
                              <li class="owner-request-li">
                                ${floorProperty.inhalt_stockwerks_einheit?.[0]?.beschreibung || ''}
                              </li>
                            `,
                          )}
                        </ul>
                      </div>
                    `
                  : ''}
                <br />
              </div>
            `;
          }
          return html``;
        })}
      </div>
    `;
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    // Check if the captcha script loaded and we need to render the widget
    if (this.captchaScriptLoaded && this.googleRecaptchaSiteKey) {
      this.renderCaptchaWidget();
    }
  }

  /**
   * Helper method to render the captcha widget when needed
   */
  renderCaptchaWidget() {
    // Wait for next microtask to ensure DOM is updated
    setTimeout(() => {
      const captchaContainer = this.renderRoot.querySelector('.g-recaptcha');
      if (window.grecaptcha && captchaContainer && !captchaContainer.querySelector('iframe')) {
        try {
          this.captchaWidgetId = window.grecaptcha.render(captchaContainer, {
            'sitekey': this.googleRecaptchaSiteKey || '',
            'callback': 'onCaptchaSuccess',
          });
        } catch (error) {
          console.error('Error rendering reCAPTCHA:', error);
        }
      }
    }, 100);
  }

  /**
   * Renders the reCAPTCHA widget based on the current state
   */
  renderCaptcha() {
    // If site key or script is not available, show spinner
    if (!this.googleRecaptchaSiteKey || !this.captchaScriptLoaded) {
      return this.renderSpinner();
    }

    // Script is loaded and site key is available
    return html`
      <br />
      <div
        class="g-recaptcha"
        data-sitekey="${this.googleRecaptchaSiteKey}"
        data-callback="onCaptchaSuccess"
      ></div>
      <br />
    `;
  }

  createRenderRoot() {
    return this;
  }
}
